// Co-located pure helpers shared across the Queue panes (list / detail / card).
import type { Job, JobStatus } from '@/lib/types'

export type Stage = 'active' | 'done' | 'cancelled'

export const STAGE_TABS: { key: Stage; label: string }[] = [
  { key: 'active', label: 'Active' },
  { key: 'done', label: 'Done' },
  { key: 'cancelled', label: 'Canceled' },
]

export const STAGE_STATUS: Record<Stage, JobStatus> = {
  active: 'ACTIVE',
  done: 'DONE',
  cancelled: 'CANCELED',
}

export function stageOf(status: JobStatus): Stage {
  switch (status) {
    case 'DONE': return 'done'
    case 'CANCELED': return 'cancelled'
    case 'ACTIVE':
    default: return 'active'
  }
}

export function statusLabel(status: JobStatus): string {
  switch (status) {
    case 'ACTIVE': return 'Active'
    case 'DONE': return 'Done'
    case 'CANCELED': return 'Canceled'
    default: return status
  }
}

export function typeLabel(t: Job['type']): string {
  switch (t) {
    case 'REPAIR': return 'Repair'
    case 'INSTALL': return 'Install'
    case 'MAINTENANCE': return 'Maintenance'
    case 'INSPECTION': return 'Inspection'
    case 'QUOTE': return 'Quote'
    case 'OTHER':
    default: return 'Other'
  }
}

/** Selectable job types for the editable Type control (value + label). */
export const JOB_TYPE_OPTIONS: { value: Job['type']; label: string }[] = [
  { value: 'REPAIR', label: 'Repair' },
  { value: 'INSTALL', label: 'Install' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'INSPECTION', label: 'Inspection' },
  { value: 'QUOTE', label: 'Quote' },
  { value: 'OTHER', label: 'Other' },
]

/** Channel a job's request came in on, derived from its linked conversation. */
export function sourceLabel(convType?: 'SMS' | 'VOICE'): string {
  if (convType === 'VOICE') return 'Phone'
  if (convType === 'SMS') return 'Text'
  return '—'
}

/** The job-site address. Prefer the job's own (editable) address; fall back to the
 *  customer's structured address on file when the job has none. A job can be at a
 *  different location than where the customer lives, so the job's field wins. */
export function joinAddress(job: Job): string {
  const jobAddr = (job.customerAddress ?? '').trim()
  if (jobAddr) return jobAddr
  const parts = [
    job.customerStreet ?? '',
    job.customerCity ?? '',
    [job.customerState ?? '', job.customerZip ?? ''].filter(Boolean).join(' '),
  ]
    .map((p) => p.trim())
    .filter(Boolean)
  return parts.join(', ')
}

/**
 * The calendar date a workspace row prints beside its title ("Sep 3"); the
 * year appears only once it differs from today's, so a feed of recent rows
 * stays short (design decision 2026-09-03, .wsFeed rows).
 */
export function formatCalendarDate(iso: string | Date | null | undefined): string {
  if (!iso) return ''
  const d = typeof iso === 'string' ? new Date(iso) : iso
  if (Number.isNaN(d.getTime())) return ''
  const sameYear = d.getFullYear() === new Date().getFullYear()
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
}
